import * as signalR from "@microsoft/signalr";
import { DataSample } from "../domain/types";
import { setLatestSample } from "../map/data/latestData";
import { loadConfiguration, showLoading } from "../map/initMap";
import { getStatus } from "./configApi";
import { updateCamera } from "../domain/visuals/sidebar";
import { getWSTicket } from "./api";

let connection: signalR.HubConnection | null = null;
let wiredHandlers = false;
let startPromise: Promise<void> | null = null;
let loaded = false;
let originalVersion = "";

const url = import.meta.env.DEV ? "https://localhost:7284/hub/Breeze" : "hub/Breeze";

export async function connectSignalR(): Promise<signalR.HubConnection> {
    if (!connection) {
        connection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
                withCredentials: true,
                accessTokenFactory: async () => await getWSTicket(),
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (ctx) => Math.min(10_000, 1000 * Math.pow(2, ctx.previousRetryCount)),
            })
            .configureLogging(signalR.LogLevel.Warning)
            .build();
    }

    if (!wiredHandlers) {
        wiredHandlers = true;

        connection.on("SourceStatusUpdated", (status: { Ready: boolean }) => {
            if (status?.Ready && !loaded) {
                loaded = true;
                loadConfiguration();
            }
        });

        connection.on("HistoricalDataCollected", (stationId) => {
            console.log("HistoricalDataCollected", stationId);
        });

        connection.on("MetadataCollected", (stationId) => {
            console.log("MetadataCollected", stationId);
        });

        connection.on("ImageCollected", (cameraId: string) => {
            updateCamera(cameraId);
        });

        connection.on("NewSamplesBulk", (stationId: string, samples: DataSample[]) => {
            for (const sample of samples ?? []) {
                if (sample.IsInstant && sample.Valid) setLatestSample(sample);
            }
        });

        connection.onreconnecting((err) => {
            console.warn("SignalR reconnecting...", err);
            showLoading("Reconnecting to Breeze Hub...");
            loaded = false;
        });

        connection.onreconnected((cid) => {
            console.info("SignalR reconnected", cid);
            showLoading("Reconnected. Syncing...");
            startup();
        });

        connection.onclose((err) => {
            console.error("SignalR closed:", err);
            showLoading("Disconnected from Breeze Hub. Will retry...");
            loaded = false;
            startPromise = startWithRetry(); // single loop owner
        });
    }

    if (!startPromise) startPromise = startWithRetry();
    await startPromise;

    return connection!;
}

function startup() {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;

    connection.invoke<string>("GetVersion").then((version) => {
        if (originalVersion === "") originalVersion = version;
        if (version !== originalVersion) {
            showLoading("New version acquired. Reloading");
            setTimeout(() => location.reload(), 1500);
            return;
        }
        void getStatus().then((p: any) => {
            if (p?.Ready && !loaded) {
                loaded = true;
                loadConfiguration();
            }
        });
    }).catch((e) => console.warn("Error loading version:", e));
}

async function startWithRetry(): Promise<void> {
    if (!connection) throw new Error("SignalR connection not built");

    let attempt = 0;
    const maxDelayMs = 10_000;

    while (connection.state !== signalR.HubConnectionState.Connected) {
        try {
            await connection.start();
            console.log("SignalR connected");
            showLoading("Connected to Breeze Hub...");
            setTimeout(() => {
                showLoading("Loading configuration...");
                startup();
            }, 300);
            return;
        } catch (err) {
            attempt++;
            const delay = Math.min(maxDelayMs, 1000 * Math.pow(2, attempt));
            console.warn(`Initial connect failed (attempt ${attempt}). Retrying in ${Math.round(delay / 1000)}s...`, err);
            showLoading(`Connecting to Breeze Hub failed. Retrying in ${Math.round(delay / 1000)}s`);
            await new Promise((r) => setTimeout(r, delay));
        }
    }
}
